import {
  disableRealtimeRaycasting,
  restoreRealtimeRaycasting,
  UPDATE_CAMERA_MONITOR_PRIORITY,
  useThreeEventTarget,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  CameraMonitor,
  MovementType,
  WalkOrbitControls,
  type PointerCoords,
} from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef, useState } from "react";
import { Object3D, Raycaster, Vector2, type Vector3 } from "three";
import { useSmartNavigationStore } from "./smart-navigation-store";

/** Diameter of the smart navigation circle, as a fraction of the smaller 3D view dimension */
export const SMART_NAVIGATION_CIRCLE_DIAMETER_RATIO = 0.4;

/** Hide the circle after this time without mouse movement, to reduce UI clutter */
const CIRCLE_IDLE_HIDE_DELAY_MS = 3000;

/** The part of the 3D view bounding rectangle needed to hit-test the smart navigation circle */
export type SmartNavigationViewRect = Pick<
  DOMRect,
  "left" | "top" | "width" | "height"
>;

/**
 * @param x Horizontal pointer position, in client coordinates
 * @param y Vertical pointer position, in client coordinates
 * @param rect Bounding rectangle of the 3D view, in client coordinates
 * @returns True if the pointer is inside the smart navigation circle centered in the given rectangle
 */
export function isPointerInsideSmartNavigationCircle(
  x: number,
  y: number,
  rect: SmartNavigationViewRect,
): boolean {
  const radius =
    (SMART_NAVIGATION_CIRCLE_DIAMETER_RATIO / 2) *
    Math.min(rect.width, rect.height);
  const dx = x - (rect.left + rect.width / 2);
  const dy = y - (rect.top + rect.height / 2);
  return dx * dx + dy * dy <= radius * radius;
}

/**
 * Implements the SCENE-like smart navigation interaction for the 3D view.
 *
 * While the mouse hovers the 3D view a circle is shown at the center of the view
 * (see SmartNavigationCircle): left-dragging inside the circle looks around like in
 * a first person game, left-dragging outside the circle orbits the camera around
 * the rotation pivot. A short click inside the circle sets the rotation pivot to
 * the clicked point and a double click flies to it, like clicking outside the
 * circle does. The circle hides while a mouse button is held and after a few
 * seconds without mouse movement.
 *
 * @param isAvailable True if smart navigation can be used in the current scene
 * @param controls The exploration controls of the current scene
 * @param clickableModels The models to raycast when looking for a new rotation pivot
 * @param setTarget Callback to set the rotation pivot
 */
export function useSmartNavigation(
  isAvailable: boolean,
  controls: WalkOrbitControls | null,
  clickableModels: Object3D[],
  setTarget?: (target: Vector3 | undefined) => void,
): void {
  const isSmartNavigationEnabled = useSmartNavigationStore(
    (state) => state.isSmartNavigationEnabled,
  );
  const eventTarget = useThreeEventTarget();
  const setShouldShowSmartNavigationCircle = useSmartNavigationStore(
    (state) => state.setShouldShowSmartNavigationCircle,
  );
  const isActive = isAvailable && isSmartNavigationEnabled;

  // Using current camera to raycast the screen center when it is needed to
  // re-determine the rotation pivot.
  const camera = useThree((s) => s.camera);

  // Using a camera monitor to detect when the camera stops
  const [cameraMonitor] = useState(() => new CameraMonitor());

  useFrame((_, delta) => {
    if (isActive) {
      cameraMonitor.checkCameraMovement(camera, delta);
    }
  }, UPDATE_CAMERA_MONITOR_PRIORITY);

  // When a rotation starts from within the circle, it rotates around the camera position.
  // When the rotation finishes, this callback is issued to reposition the rotation pivot
  // on a valid 3D point.
  const raycastNewTarget = useCallback(() => {
    if (setTarget === undefined) {
      return;
    }
    const raycaster = new Raycaster();
    // Raycasting the clickable models at the center of the screen (0,0 in NDC)
    raycaster.setFromCamera(new Vector2(0, 0), camera);

    const toRestore = disableRealtimeRaycasting(clickableModels);
    const hits = raycaster.intersectObjects(clickableModels);
    restoreRealtimeRaycasting(toRestore);
    if (hits.length === 0) {
      return;
    }

    for (const hit of hits) {
      // Discard hits too close to the camera
      if (hit.distance < 0.1) continue;

      // Compute clicked point, need to clone here to not modify raycaster ray in-place
      const newTarget = raycaster.ray.origin
        .clone()
        .add(raycaster.ray.direction.multiplyScalar(hit.distance));

      setTarget(newTarget);
      break;
    }
  }, [clickableModels, setTarget, camera]);

  const lastTarget = useRef<Vector3 | undefined>(undefined);

  const isSameTarget = useCallback((target: Vector3 | undefined) => {
    const last = lastTarget.current;
    if (!last && !target) return true;
    if (!last || !target) return false;
    return last.distanceTo(target) < 0.001;
  }, []);

  const onCameraStartedMoving = useCallback(() => {
    const target = controls?.target;
    if (target) {
      lastTarget.current = target.clone();
    } else {
      lastTarget.current = undefined;
    }
  }, [controls]);

  const onCameraStopped = useCallback(() => {
    if (!isActive) return;
    const target = controls?.target;
    if (!isSameTarget(target)) {
      raycastNewTarget();
    }
  }, [raycastNewTarget, controls, isSameTarget, isActive]);

  useTypedEvent(cameraMonitor.cameraStartedMoving, onCameraStartedMoving);
  useTypedEvent(cameraMonitor.cameraStoppedMoving, onCameraStopped);

  useEffect(() => {
    if (!isActive || !controls) return;
    const activeControls = controls;

    // The camera must stop exactly when the user releases the mouse, so disable
    // all mouse inertia while smart navigation is active. Both inertias matter
    // for the look-around: the controls record the pivot re-centering done by
    // updateFromCamera() as a translation speed, so with movement inertia the
    // camera would keep translating after the mouse is released.
    // Wheel and keyboard movements don't use the mouse inertia and are unaffected.
    const { mouseSettings } = activeControls;
    const originalMovementInertia = mouseSettings.movementInertia;
    const originalRotationInertia = mouseSettings.rotationInertia;
    mouseSettings.movementInertia = 0;
    mouseSettings.rotationInertia = 0;

    // Pointer state the circle visibility and the camera mode icon are derived from
    let isPointerHovering = false;
    let isPointerInside = false;
    let isPointerPressed = false;

    function publishPointerState(): void {
      setShouldShowSmartNavigationCircle(
        isPointerHovering && !isPointerPressed,
      );
    }

    // Handle of the timer that hides the circle when the mouse is idle
    let idleHideTimeout: number | undefined;

    function clearIdleHideTimer(): void {
      if (idleHideTimeout !== undefined) {
        window.clearTimeout(idleHideTimeout);
        idleHideTimeout = undefined;
      }
    }

    function restartIdleHideTimer(): void {
      clearIdleHideTimer();
      idleHideTimeout = window.setTimeout(() => {
        idleHideTimeout = undefined;
        isPointerHovering = false;
        publishPointerState();
      }, CIRCLE_IDLE_HIDE_DELAY_MS);
    }

    // Track mouse movement to determine whether the mouse is inside or outside the
    // smart navigation circle.
    function onPointerMove(ev: PointerEvent): void {
      if (ev.pointerType !== "mouse") return;
      isPointerHovering = true;
      isPointerInside = isPointerInsideSmartNavigationCircle(
        ev.clientX,
        ev.clientY,
        eventTarget.getBoundingClientRect(),
      );
      publishPointerState();
      // Hide the circle again after a while without mouse movement
      restartIdleHideTimer();
    }

    function onPointerLeave(): void {
      if (document.pointerLockElement === eventTarget) return;
      clearIdleHideTimer();
      isPointerHovering = false;
      isPointerInside = false;
      publishPointerState();
    }

    // When the user starts a rotation with the mouse, determine
    // whether the mouse was clicked inside or outside the circle,
    // and set 'rotateAlwaysAroundCameraPos' accordingly.
    function rotationStarted(type: MovementType): void {
      isPointerPressed = true;
      if (type !== MovementType.Rotating) return;
      activeControls.rotateAlwaysAroundCameraPos = isPointerInside;
    }

    function rotationEnded(): void {
      isPointerPressed = false;
      handleLookAroundEnded();
    }

    function handleLookAroundEnded(): void {
      if (activeControls.rotateAlwaysAroundCameraPos) {
        activeControls.rotateAlwaysAroundCameraPos = false;
      }
    }

    // When the user starts a new touch-drag interaction, determine
    // whether the touch was inside or outside the circle,
    // and set 'rotateAlwaysAroundCameraPos' accordingly.
    function touchStarted(ev: PointerCoords): void {
      isPointerInside = isPointerInsideSmartNavigationCircle(
        ev.clientPosition.x,
        ev.clientPosition.y,
        eventTarget.getBoundingClientRect(),
      );
      activeControls.rotateAlwaysAroundCameraPos = isPointerInside;
      isPointerPressed = true;
      isPointerHovering = true;
      publishPointerState();
    }

    function touchEnded(): void {
      isPointerHovering = true;
      isPointerPressed = false;
      publishPointerState();
      handleLookAroundEnded();
    }

    eventTarget.addEventListener("pointermove", onPointerMove);
    eventTarget.addEventListener("pointerleave", onPointerLeave);
    activeControls.movementStarted.on(rotationStarted);
    activeControls.movementEnded.on(rotationEnded);
    activeControls.touchEvents.touchStarted.on(touchStarted);
    activeControls.touchEvents.touchEnded.on(touchEnded);
    return () => {
      eventTarget.removeEventListener("pointermove", onPointerMove);
      eventTarget.removeEventListener("pointerleave", onPointerLeave);
      activeControls.movementStarted.off(rotationStarted);
      activeControls.movementEnded.off(rotationEnded);
      activeControls.touchEvents.touchStarted.off(touchStarted);
      activeControls.touchEvents.touchEnded.off(touchEnded);
      activeControls.rotateAlwaysAroundCameraPos = false;
      mouseSettings.movementInertia = originalMovementInertia;
      mouseSettings.rotationInertia = originalRotationInertia;
      setShouldShowSmartNavigationCircle(false);
      clearIdleHideTimer();
    };
  }, [
    isActive,
    controls,
    eventTarget,
    setShouldShowSmartNavigationCircle,
    raycastNewTarget,
    cameraMonitor,
  ]);
}
